import { FinancialDetails } from "./FinancialDetails";
import { formatDate, translate, formatAddress } from "@/utils/formatters";
import { Article, Receipt } from "@/types/receipt";

import styles from "./ArticleDetails.module.css";

interface ArticleDetailsProps {
  article: Article;
  lang: string;
  receipt: Receipt;
  expanded: boolean;
}

export const ArticleDetails = ({
  article,
  lang,
  receipt,
  expanded,
}: ArticleDetailsProps) => {
  if (!expanded) return null;

  const details = [
    {
      condition: article.art_number,
      label: translate(lang, "Artikkelnr: ", "Article number: "),
      value: article.art_number,
    },
    {
      condition: article.art_description,
      label: translate(lang, "Beskrivelse: ", "Description: "),
      value: article.art_description,
    },
    {
      condition: article.reason_for_return,
      label: translate(lang, "Årsak for retur: ", "Reason for return: "),
      value: article.reason_for_return,
    },
    {
      condition: article.expired_at,
      label: translate(lang, "Utløpsdato: ", "Expired at: "),
      value: formatDate(
        article.expired_at,
        receipt.merchant.country_code,
        false
      ),
    },
    {
      condition: article.location?.departure_reference,
      label: translate(lang, "Avgangsreferanse: ", "Departure reference: "),
      value: article.location?.departure_reference,
    },
    {
      condition: article.location?.arrival_reference,
      label: translate(lang, "Ankomstreferanse: ", "Arrival reference: "),
      value: article.location?.arrival_reference,
    },
    {
      condition: article.location?.start_address,
      label: translate(lang, "Startadresse: ", "Start address: "),
      value: article.location?.start_address
        ? formatAddress(article.location.start_address)
        : "",
    },
    {
      condition: article.location?.end_address,
      label: translate(lang, "Sluttadresse: ", "End address: "),
      value: article.location?.end_address
        ? formatAddress(article.location.end_address)
        : "",
    },
    {
      condition: article.location?.distance_value,
      label: translate(lang, "Lengde: ", "Travel distance: "),
      value: article.location?.distance_value
        ? `${article.location.distance_value} ${
            article.location.distance_value_definition ?? ""
          }`
        : "",
    },
    {
      condition: article.event_start_at,
      label:
        article.type === "travel_ticket"
          ? translate(
              lang,
              "ESTIMERT AVGANGSTID: ",
              "ESTIMATED TIME OF DEPARTURE: "
            )
          : translate(lang, "Starter: ", "Starts at: "),
      value: formatDate(
        article.event_start_at,
        receipt.total.merchant_currency
      ),
    },
    {
      condition: article.event_ending_at,
      label:
        article.type === "travel_ticket"
          ? translate(
              lang,
              "ESTIMERT ANKOMSTTID: ",
              "ESTIMATED TIME OF ARRIVAL: "
            )
          : translate(lang, "Slutter: ", "Ends at: "),
      value: formatDate(
        article.event_ending_at,
        receipt.total.merchant_currency
      ),
    },
    {
      condition: article.specifics?.entrance,
      label: translate(lang, "Inngang: ", "Entrance: "),
      value: article.specifics?.entrance,
    },
    {
      condition: article.specifics?.row_number,
      label: translate(lang, "Radnummer: ", "Row number: "),
      value: article.specifics?.row_number,
    },
    {
      condition: article.specifics?.seat_number,
      label: translate(lang, "Setenummer: ", "Seat number: "),
      value: article.specifics?.seat_number,
    },
    {
      condition: article.specifics?.floor_number,
      label: translate(lang, "Etasjenummer: ", "Floor number: "),
      value: article.specifics?.floor_number,
    },
    {
      condition: article.specifics?.room_number,
      label: translate(lang, "Romnummer: ", "Room number: "),
      value: article.specifics?.room_number,
    },
  ];

  return (
    <div className="article-details">
      {details.map(
        ({ condition, label, value }) =>
          condition != null && condition !== "" && (
            <h5 key={`detail-${label}`}>
              <strong className={styles.detailLabel}>{label}</strong>
              <span className={styles.detailValue}>{value}</span>
            </h5>
          )
      )}
      <FinancialDetails article={article} lang={lang} receipt={receipt} />
    </div>
  );
};
